import cuid from "cuid";
import { cloneDeep, get, isEmpty, isNumber, set } from "lodash";
import moment from "moment";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "shared/hooks/useApi";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import { CREATE_DISTRIBUTION } from "utils/api/graphql/mutations/distributions";
import {
  DELTA_END_TYPES,
  INITIAL_VALUES,
  INSURANCE_PLAN_PARAMS_RULES,
  PERIOD_UNITS,
  SCREENS,
} from "utils/constants";
import View from "./View";

const DELTA_END_VALUES = {
  [DELTA_END_TYPES.END_OF_YEAR]: () => 0,
  [DELTA_END_TYPES.ADD_PERIOD]: (start, end, unit = PERIOD_UNITS.DAYS) => {
    if (!isNumber(start) || !isNumber(end)) return null;
    return moment()
      .add(start || 0, "days")
      .add(end, unit)
      .diff(moment().startOf("day"), "days");
  },
  DEFAULT: () => null,
};
const updateRulesWithDeltaValues = (rules) => {
  const newRules = cloneDeep(rules);

  const deltaEndValue = calculateDeltaEndValue(
    newRules,
    INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
    INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
  );
  if (deltaEndValue !== null) {
    set(
      newRules,
      `${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.value`,
      deltaEndValue,
    );
  }

  const deltaEndRiaValue = calculateDeltaEndValue(
    newRules,
    INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
    INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
  );
  if (deltaEndRiaValue !== null) {
    set(
      newRules,
      `${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.value`,
      deltaEndRiaValue,
    );
  }
  return newRules;
};

const calculateDeltaEndValue = (rules, startRuleKey, endRuleKey) => {
  const type = get(rules, `${endRuleKey}.type`) || "DEFAULT";
  return DELTA_END_VALUES[type](
    get(rules, `${startRuleKey}.value`),
    get(rules, `${endRuleKey}.value`),
    get(rules, `${endRuleKey}.unit`),
  );
};

const convertRulesToEntries = (rules) => {
  return Object.entries(rules).map(([name, data]) => ({
    id: get(data, "id") || cuid(),
    name,
    value: get(data, "value"),
  }));
};

const CreateHealth = ({ risk }) => {
  const navigate = useNavigate();
  const id = useMemo(() => cuid(), []);
  const { user } = useContext(StoreContext);

  const [addDistribution] = useMutation(CREATE_DISTRIBUTION);
  const handleDistribution = (internal) => {
    const distribution = {
      [true]: () =>
        addDistribution({
          variables: {
            data: {
              id: cuid(),
              insurancePlan: { id },
              broker: { id: get(user, "organization.broker.id") },
            },
          },
        }),
      [false]: () => null,
    };
    return distribution[internal]();
  };
  const cancel = () =>
    navigate(SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.LIST.path);
  const [addInsurancePlan] = useMutation(
    graphql.mutations.CREATE_INSURANCE_PLAN,
    {
      refetchQueries: [
        {
          query: graphql.queries.INSURANCE_PLANS,
          awaitRefetchQueries: true,
          variables: INITIAL_VALUES,
        },
      ],
      onCompleted: ({ addInsurancePlan }) => {
        handleDistribution(addInsurancePlan?.internal);
        navigate(SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.LIST.path);
      },
      onError: () => {},
    },
  );

  const onSubmit = (values) => {
    const {
      date,
      deltaEndType,
      deltaEndUnit,
      params,
      iconAttachment,
      icon,
      commissions,
      level,
      ...rest
    } = values;
    const updatedParams = params
      .map((item) => ({
        id: item.id,
        rules: item.rules,
      }))
      .filter((item) => !isEmpty(item.rules))
      .map(({ id, rules }) => {
        return {
          id,
          rules: convertRulesToEntries(
            updateRulesWithDeltaValues(rules),
          )?.filter(({ value }) => isNumber(value) || !isEmpty(value)),
        };
      });

    addInsurancePlan({
      variables: {
        data: {
          ...rest,
          id,
          identifier: cuid(),
          startDate: get(date, "0"),
          endDate: get(date, "1"),
          icon: iconAttachment,
          commissions: commissions.map((commission) => ({ id: commission })),
          level: isNumber(level) ? level : null,
          params: updatedParams,
          risk,
        },
      },
    });
  };
  return <View onSubmit={onSubmit} id={id} cancel={cancel} risk={risk} />;
};

export default CreateHealth;
